/**
 * Organization Sentiment Heatmap — sentiment-stats/2.0 client
 * (v2.5 SS-03 / PR C2).
 *
 * Persona × FAIR-pillar matrix over the server's 2.0 statistics. The
 * client RENDERS; it never re-derives a statistic the server suppressed:
 *
 *   - `null` = NO BASIS and renders the em dash (—) with a screen-reader
 *     label; `0.00` is a MEASURED neutral and renders as a value. The two
 *     never conflate (G0 §7.2).
 *   - Dispersion (cell stdDev, pillar variance) and consensus arrive
 *     pre-suppressed (null below k/n = 2) — the 1.x client computed its own
 *     pillar variance and applied its own gate, which is how one pillar
 *     suppressed at k=2 while another computed.
 *   - Counts are NAMED units: interviews and speakers are stated
 *     separately, personas exclude unattributed role buckets.
 *
 * Sprint 9 - Organization-Wide Sentiment
 * Reworked v2.5 - sentiment-stats/2.0 (SS-03 C2)
 */

import React, { useState, useEffect } from 'react';
import { SentimentBadge } from './sentiment/SentimentBadge';
import {
  formatScore,
  getPersonaAbbrev,
  getSentimentColor,
  getTextColor,
} from './sentiment/sentimentScale';
import './org-sentiment-heatmap.css';

// Types matching the server response (sentiment-stats/2.0)
interface HeatmapCell {
  sentiment: number | null;
  sampleSize: number;
  stdDev: number | null;
}

interface AttributedQuote {
  text: string;
  sentiment: number;
  persona: string;
  pillar: string;
  interviewId: string;
  interviewDate?: string;
}

interface PillarAverage {
  sentiment: number | null;
  sampleSize: number;
  contributors: number;
  variance: number | null;
  consensus: number | null;
  topPersona: string | null;
  bottomPersona: string | null;
}

interface OrgSentimentData {
  heatmap: Record<string, Record<string, HeatmapCell>>;
  rows: Record<string, { personaId: string | null; label: string }>;
  pillarAverages: Record<string, PillarAverage>;
  overall: {
    sentiment: number | null;
    topPainPoint: string | null;
    topStrength: string | null;
    consensusLevel: number | null;
    emotionalIntensity: number | null;
  };
  topQuotes: {
    positive: AttributedQuote[];
    negative: AttributedQuote[];
  };
  metadata: {
    methodology: string;
    totalInterviews: number;
    analyzedInterviews: number;
    analyzedSpeakers: number;
    contributors: number;
    observations: number;
    personaCount: number;
    unattributedBuckets: number;
    lastUpdated: string;
    pillarCoverage: Record<string, number>;
  };
}

const FAIR_PILLARS = ['Findable', 'Accessible', 'Interoperable', 'Reusable'] as const;

interface PillarDisclosureProps {
  pillar: string;
  pillarAverage: PillarAverage;
  positiveQuotes: AttributedQuote[];
  negativeQuotes: AttributedQuote[];
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Single per-pillar disclosure row used inside the drill-down section.
 * Dispersion + consensus badges render ONLY when the server computed them
 * (k ≥ 2) — the suppression decision is the server's, stated once.
 */
function PillarDisclosure({
  pillar,
  pillarAverage,
  positiveQuotes,
  negativeQuotes,
  isOpen,
  onToggle,
}: PillarDisclosureProps) {
  const hasData = pillarAverage.sentiment !== null;
  const bodyId = `pillar-body-${pillar}`;
  return (
    <div className={`pillar-disclosure ${isOpen ? 'pillar-disclosure--open' : ''}`}>
      <button
        type="button"
        className="pillar-disclosure__header"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className="pillar-disclosure__letter">{pillar[0]}</span>
        <span className="pillar-disclosure__name">{pillar}</span>
        <span
          className="pillar-disclosure__score"
          style={{
            color: hasData ? getSentimentColor(pillarAverage.sentiment as number) : 'var(--tri-quiet)',
          }}
        >
          {hasData ? formatScore(pillarAverage.sentiment as number) : '—'}
        </span>
        <span className="pillar-disclosure__badges">
          <SentimentBadge variant="sample-size" value={pillarAverage.sampleSize} />
          {pillarAverage.variance !== null && (
            <SentimentBadge
              variant="variance"
              value={pillarAverage.variance}
              total={pillarAverage.contributors}
            />
          )}
          {pillarAverage.consensus !== null && (
            <SentimentBadge variant="consensus" value={pillarAverage.consensus} />
          )}
        </span>
        <svg
          className={`pillar-disclosure__chevron ${isOpen ? 'pillar-disclosure__chevron--open' : ''}`}
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div id={bodyId} className="pillar-disclosure__body" hidden={!isOpen}>
        <div className="pillar-disclosure__column pillar-disclosure__column--strengths">
          <h5>Strengths</h5>
          {positiveQuotes.length > 0 ? (
            positiveQuotes.map((quote, idx) => (
              <div key={`pos-${idx}`} className="quote-item quote-item--positive">
                <p className="quote-text">&quot;{quote.text}&quot;</p>
                <div className="quote-meta">
                  <span className="quote-persona">{quote.persona}</span>
                  <span className="quote-score">{formatScore(quote.sentiment)}</span>
                </div>
              </div>
            ))
          ) : (
            <p className="pillar-disclosure__empty">
              No notable strengths surfaced for {pillar} yet ({pillarAverage.sampleSize} observations).
            </p>
          )}
        </div>
        <div className="pillar-disclosure__column pillar-disclosure__column--painpoints">
          <h5>Pain Points</h5>
          {negativeQuotes.length > 0 ? (
            negativeQuotes.map((quote, idx) => (
              <div key={`neg-${idx}`} className="quote-item quote-item--negative">
                <p className="quote-text">&quot;{quote.text}&quot;</p>
                <div className="quote-meta">
                  <span className="quote-persona">{quote.persona}</span>
                  <span className="quote-score">{formatScore(quote.sentiment)}</span>
                </div>
              </div>
            ))
          ) : (
            <p className="pillar-disclosure__empty">
              No notable pain points surfaced for {pillar} yet ({pillarAverage.sampleSize} observations).
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface OrgSentimentHeatmapProps {
  className?: string;
}

export function OrgSentimentHeatmap({ className }: OrgSentimentHeatmapProps) {
  const [data, setData] = useState<OrgSentimentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCell, setActiveCell] = useState<{ persona: string; pillar: string } | null>(null);
  const [openPillar, setOpenPillar] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'xlsx' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    loadHeatmap();
  }, []);

  /**
   * Download the org-wide aggregate export (DC-1759).
   *
   * Kept separate from the load-error state on purpose: a failed export must
   * not replace the heatmap the user is looking at with an error screen.
   */
  const handleExport = async (format: 'pdf' | 'xlsx') => {
    setExportingFormat(format);
    setExportError(null);
    try {
      const response = await fetch(
        `/api/organization/interviews/heatmap/report?format=${format}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error?.message || `Failed to export (HTTP ${response.status})`,
        );
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `Organization_Sentiment_Analysis.${format}`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('[OrgSentimentHeatmap] Export error:', err);
      setExportError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setExportingFormat(null);
    }
  };

  const loadHeatmap = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/organization/interviews/heatmap');
      if (!response.ok) {
        throw new Error(`Failed to load heatmap: ${response.statusText}`);
      }
      const result = await response.json();
      setData(result.heatmap);
    } catch (err: unknown) {
      console.error('[OrgSentimentHeatmap] Load error:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`org-heatmap ${className || ''}`}>
        <div className="org-heatmap__loading">Loading organization sentiment data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`org-heatmap ${className || ''}`}>
        <div className="org-heatmap__error">
          <p>Error loading heatmap: {error}</p>
          <button onClick={loadHeatmap} className="btn btn--primary">Retry</button>
        </div>
      </div>
    );
  }

  if (!data || data.metadata.contributors === 0) {
    return (
      <div className={`org-heatmap ${className || ''}`}>
        <div className="org-heatmap__empty">
          <h3>No Sentiment Data Available</h3>
          <p>Upload and analyze interviews to see the organization sentiment heatmap.</p>
        </div>
      </div>
    );
  }

  const personas = Object.keys(data.heatmap).sort();
  const activeData = activeCell
    ? data.heatmap[activeCell.persona]?.[activeCell.pillar]
    : null;

  // NAMED units in the header — interviews and speakers stated separately;
  // persona count excludes unattributed role buckets (G0 §7.2).
  const sourceParts: string[] = [];
  if (data.metadata.analyzedInterviews > 0) {
    sourceParts.push(
      `${data.metadata.analyzedInterviews} interview${data.metadata.analyzedInterviews === 1 ? '' : 's'}`,
    );
  }
  if (data.metadata.analyzedSpeakers > 0) {
    sourceParts.push(
      `${data.metadata.analyzedSpeakers} group-session speaker${data.metadata.analyzedSpeakers === 1 ? '' : 's'}`,
    );
  }
  const subtitle =
    `${sourceParts.join(' + ')} analyzed across ${data.metadata.personaCount} persona${data.metadata.personaCount === 1 ? '' : 's'}` +
    (data.metadata.unattributedBuckets > 0
      ? ` (+${data.metadata.unattributedBuckets} unattributed role${data.metadata.unattributedBuckets === 1 ? '' : 's'})`
      : '');

  return (
    <div className={`org-heatmap ${className || ''}`}>
      {/* Header with overall metrics — null renders — (no basis), never 0 */}
      <div className="org-heatmap__header">
        <div className="org-heatmap__title">
          <h3>Organization Sentiment Heatmap</h3>
          <span className="org-heatmap__subtitle">{subtitle}</span>
        </div>
        <div className="org-heatmap__metrics">
          <div className="metric">
            <span className="metric__label">Overall</span>
            {data.overall.sentiment !== null ? (
              <span
                className="metric__value"
                style={{ color: getSentimentColor(data.overall.sentiment) }}
              >
                {formatScore(data.overall.sentiment)}
              </span>
            ) : (
              <span className="metric__value metric__value--none" aria-label="Not assessable, no basis">—</span>
            )}
          </div>
          <div className="metric metric--consensus">
            <span className="metric__label">Consensus</span>
            {data.overall.consensusLevel !== null ? (
              <SentimentBadge variant="consensus" value={data.overall.consensusLevel} />
            ) : (
              <span className="metric__value metric__value--none" aria-label="Not assessable, fewer than 2 contributing personas">—</span>
            )}
          </div>
          <div className="metric metric--tag metric--pain">
            <span className="metric__label">Top Pain Point</span>
            {data.overall.topPainPoint !== null ? (
              <span className="metric__tag">
                <span className="metric__tag-letter">{data.overall.topPainPoint[0]}</span>
                <span className="metric__tag-name">{data.overall.topPainPoint}</span>
              </span>
            ) : (
              <span className="metric__value metric__value--none" aria-label="Not assessable, no basis">—</span>
            )}
          </div>
          <div className="metric metric--tag metric--strength">
            <span className="metric__label">Top Strength</span>
            {data.overall.topStrength !== null ? (
              <span className="metric__tag">
                <span className="metric__tag-letter">{data.overall.topStrength[0]}</span>
                <span className="metric__tag-name">{data.overall.topStrength}</span>
              </span>
            ) : (
              <span className="metric__value metric__value--none" aria-label="Not assessable, no basis">—</span>
            )}
          </div>
        </div>
        {/* Aggregate export (DC-1759) — PDF for the readout, Excel for analysis. */}
        <div className="org-heatmap__actions">
          <button
            type="button"
            className="org-heatmap__export-btn"
            onClick={() => handleExport('pdf')}
            disabled={exportingFormat !== null}
            data-testid="export-org-sentiment-pdf"
          >
            {exportingFormat === 'pdf' ? 'Exporting…' : 'Export PDF'}
          </button>
          <button
            type="button"
            className="org-heatmap__export-btn"
            onClick={() => handleExport('xlsx')}
            disabled={exportingFormat !== null}
            data-testid="export-org-sentiment-xlsx"
          >
            {exportingFormat === 'xlsx' ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>
      {exportError && (
        <p className="org-heatmap__export-error" role="alert">
          {exportError}
        </p>
      )}

      {/* Main heatmap grid */}
      <div className="org-heatmap__grid-container">
        <div className="org-heatmap__grid">
          {/* Header row with pillar names */}
          <div className="heatmap-row heatmap-row--header">
            <div className="heatmap-cell heatmap-cell--corner">Persona</div>
            {FAIR_PILLARS.map(pillar => (
              <div key={pillar} className="heatmap-cell heatmap-cell--header">
                <span className="pillar-letter">{pillar[0]}</span>
                <span className="pillar-name">{pillar}</span>
              </div>
            ))}
          </div>

          {/* Persona rows */}
          {personas.map(persona => (
            <div key={persona} className="heatmap-row">
              <div className="heatmap-cell heatmap-cell--persona">
                <span className="persona-abbrev">{getPersonaAbbrev(persona)}</span>
                <span className="persona-full">{persona}</span>
              </div>
              {FAIR_PILLARS.map(pillar => {
                const cell = data.heatmap[persona]?.[pillar];
                const measured = cell !== undefined && cell.sentiment !== null;
                const isActive = activeCell?.persona === persona && activeCell?.pillar === pillar;

                return (
                  <div
                    key={pillar}
                    className={`heatmap-cell heatmap-cell--data ${measured ? '' : 'heatmap-cell--nobasis'} ${isActive ? 'heatmap-cell--hovered' : ''}`}
                    style={{
                      backgroundColor: measured
                        ? getSentimentColor(cell.sentiment as number)
                        : 'var(--tri-track)',
                      color: measured ? getTextColor(cell.sentiment as number) : 'var(--tri-quiet)'
                    }}
                    tabIndex={0}
                    onMouseEnter={() => setActiveCell({ persona, pillar })}
                    onMouseLeave={() => setActiveCell(null)}
                    onFocus={() => setActiveCell({ persona, pillar })}
                    onBlur={() => setActiveCell(null)}
                  >
                    {measured ? (
                      <>
                        <span className="cell-score">{formatScore(cell.sentiment as number)}</span>
                        <span className="cell-sample">n={cell.sampleSize}</span>
                      </>
                    ) : (
                      <span className="cell-empty" role="img" aria-label="Not assessable, no basis">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Footer row with pillar averages */}
          <div className="heatmap-row heatmap-row--footer">
            <div className="heatmap-cell heatmap-cell--corner">Average</div>
            {FAIR_PILLARS.map(pillar => {
              const avg = data.pillarAverages[pillar];
              const measured = avg?.sentiment !== null && avg !== undefined;
              return (
                <div
                  key={pillar}
                  className="heatmap-cell heatmap-cell--average"
                  style={{
                    backgroundColor: measured ? getSentimentColor(avg.sentiment as number) : 'var(--tri-track)',
                    color: measured ? getTextColor(avg.sentiment as number) : 'var(--tri-quiet)'
                  }}
                >
                  <span className="cell-score">
                    {measured ? formatScore(avg.sentiment as number) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail panel — reachable by keyboard focus as well as hover */}
        {activeCell && activeData && (
          <div className="org-heatmap__detail">
            <h4>{activeCell.persona}</h4>
            <p className="detail-pillar">{activeCell.pillar}</p>
            <div className="detail-stats">
              <div className="stat">
                <span className="stat-label">Sentiment</span>
                {activeData.sentiment !== null ? (
                  <span className="stat-value" style={{ color: getSentimentColor(activeData.sentiment) }}>
                    {formatScore(activeData.sentiment)}
                  </span>
                ) : (
                  <span className="stat-value">—</span>
                )}
              </div>
              <div className="stat">
                <span className="stat-label">Observations</span>
                <span className="stat-value">{activeData.sampleSize}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Std Dev</span>
                <span className="stat-value">
                  {activeData.stdDev !== null ? activeData.stdDev.toFixed(2) : '—'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="org-heatmap__legend">
        <span className="legend-label">Sentiment:</span>
        <div className="legend-scale">
          {/* Ink per fill from the mandated pairing table (getTextColor) —
              the CSS white default failed on p1 (3.03:1) and neutral (2.50:1). */}
          {[-1.0, -0.5, -0.2, 0, 0.2, 0.5, 1.0].map((v) => (
            <div
              key={v}
              className="legend-item"
              style={{ backgroundColor: getSentimentColor(v), color: getTextColor(v) }}
            >
              {v === 0 ? '0' : v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1)}
            </div>
          ))}
        </div>
      </div>

      {/* Per-pillar quote drill-down (DC-1573 Half A — AC#1/#2/#3) */}
      <div className="org-heatmap__pillars">
        <div className="org-heatmap__pillars-intro">
          <h4>Pillar Drill-Down</h4>
          <p className="org-heatmap__pillars-hint">
            Expand a pillar to read the strongest signals across all personas.
          </p>
        </div>
        {FAIR_PILLARS.map((pillar) => {
          const avg: PillarAverage = data.pillarAverages[pillar] ?? {
            sentiment: null,
            sampleSize: 0,
            contributors: 0,
            variance: null,
            consensus: null,
            topPersona: null,
            bottomPersona: null,
          };
          const positiveForPillar = data.topQuotes.positive
            .filter(q => q.pillar === pillar)
            .slice(0, 3);
          const negativeForPillar = data.topQuotes.negative
            .filter(q => q.pillar === pillar)
            .slice(0, 3);
          return (
            <PillarDisclosure
              key={pillar}
              pillar={pillar}
              pillarAverage={avg}
              positiveQuotes={positiveForPillar}
              negativeQuotes={negativeForPillar}
              isOpen={openPillar === pillar}
              onToggle={() => setOpenPillar(openPillar === pillar ? null : pillar)}
            />
          );
        })}
      </div>

      {/* Metadata footer — names the methodology that produced the stats */}
      <div className="org-heatmap__footer">
        <span>Last updated: {new Date(data.metadata.lastUpdated).toLocaleString()}</span>
        <span>Coverage: F:{data.metadata.pillarCoverage.Findable}% A:{data.metadata.pillarCoverage.Accessible}% I:{data.metadata.pillarCoverage.Interoperable}% R:{data.metadata.pillarCoverage.Reusable}%</span>
        <span>Methodology: {data.metadata.methodology}</span>
      </div>
    </div>
  );
}

export default OrgSentimentHeatmap;
